import { DOCUMENT } from '@angular/common';
import { AfterViewInit, Directive, ElementRef, inject, Input, TransferState } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivationStart, Navigation, Router } from '@angular/router';
import { filter, withLatestFrom } from 'rxjs/operators';

import { HeaderService } from '@usheru-hff/sdk/header';
import { MOVIE_TRANSFER_KEY, MovieService } from '@usheru-hff/sdk/movie';
import { Movie } from '@usheru-hff/shared/domain';

/**
 * Directive to attach to the **page-wrapper** element in the **app.component**
 * If this element has already other classes related to the routes, that logic in
 * the app.component should be removed to be replaced by this directive.
 */
@Directive({
  selector: '[appPageWrapper]'
})
export class PageWrapperDirective implements AfterViewInit {
  private readonly elementRef: ElementRef<HTMLDivElement> = inject(ElementRef);
  private readonly router = inject(Router);
  private readonly headerService = inject(HeaderService);
  private readonly transferState = inject(TransferState);
  private readonly document = inject(DOCUMENT);
  constructor() {
    this.router.events
      .pipe(
        takeUntilDestroyed(),
        filter((routeEvent): routeEvent is ActivationStart => routeEvent instanceof ActivationStart)
      )
      .subscribe(event => this.checkActivationStart(event, this.transferState.get(MOVIE_TRANSFER_KEY, null)));
  }

  /**
   * Some apps has the *route classes* in two places. Maybe this is not necesary but it will prevent
   * some issues with the current style implementation.
   * This is a selector for the *querySelector*.
   */
  @Input() extraElementToSetRouteClasses: string = '';

  initialClasses: string = '';
  initialClassesExtraElement = '';

  ngAfterViewInit() {
    this.listenRouterEvents();
  }

  listenRouterEvents() {
    this.initialClasses = `${this.element.classList.value}`;
    if (this.extraElementToSetRouteClasses) {
      const extraElement = this.element.querySelector(this.extraElementToSetRouteClasses) as HTMLElement;
      if (extraElement) {
        this.initialClassesExtraElement = `${extraElement.classList.value}`;
      }
    }
  }

  checkActivationStart(event: ActivationStart, movieFromSSR: Movie | undefined) {
    const snapshot = event.snapshot;

    const template = snapshot.data?.template ? `route-template-${snapshot.data.template}` : null;
    const identifier = snapshot.data?.identifier ? `route-${snapshot.data.identifier}` : null;
    const type = snapshot.data?.type ? `route-${snapshot.data.type}` : null;

    let activatedPath = 'route';
    if (type === 'route-movie') {
      // It could use the else condition but for movie cases we don't want the subroutes
      activatedPath = `${activatedPath}-${snapshot.data['movieSlug']}`;
    } else {
      activatedPath = `${activatedPath}-${snapshot.url.join('-')}`;
    }

    const routeClasses = [activatedPath, template, identifier, type].filter(Boolean).join(' ');

    this.setRouteClass(routeClasses);

    this.setNoGlobalStylingClass(snapshot.data?.noGlobalStyling ? true : false, movieFromSSR);

    if (this.extraElementToSetRouteClasses) {
      this.setExtraRouteClass(routeClasses);
    }
  }

  setRouteClass(classes: string) {
    this.element.classList.value = `${this.initialClasses} ${classes}`;
  }

  /**
   * Related to the *extraElementToSetRouteClasses* property
   */
  setExtraRouteClass(classes: string) {
    if (this.extraElementToSetRouteClasses) {
      const extraElement = this.element.querySelector(this.extraElementToSetRouteClasses) as HTMLElement;
      if (extraElement) {
        extraElement.classList.value = `${this.initialClassesExtraElement} ${classes}`;
      }
    }
  }

  /**
   * Put after *setRouteClass*. If not this rule will be overriden.
   */
  setNoGlobalStylingClass(isMoviePage: boolean, movieFromSSR: Movie | undefined) {
    this.element.classList.toggle('noGlobalStyling', isMoviePage);
    if (isMoviePage && movieFromSSR?.settings?.movieSiteEnabled) {
      this.headerService.setDisplayStyle('none');
    }
  }

  get element() {
    return this.elementRef.nativeElement;
  }
}
